(() => {
  'use strict';

  const SELECTORS = {
    loader: '#SignatureLoader',
    mark: '.signature-loader__mark',
    maskGuides: '.signature-loader__mask-guide',
    swimwear: '.signature-loader__swimwear',
    bead: '.signature-loader__ink-bead',
  };

  const STORAGE_KEY = 'sylavieSignatureIntroPlayed';
  const ROOT_CLASSES = ['signature-loader-pending', 'signature-loader-active'];
  const MAX_LOCK_DURATION = 4000;

  class SylavieSignatureLoader {
    constructor(element) {
      this.element = element;
      this.root = document.documentElement;
      this.mark = element.querySelector(SELECTORS.mark);
      this.maskGuides = Array.from(element.querySelectorAll(SELECTORS.maskGuides));
      this.swimwear = element.querySelector(SELECTORS.swimwear);
      this.bead = element.querySelector(SELECTORS.bead);
      this.animations = [];
      this.guideTimers = [];
      this.beadFrame = null;
      this.destroyed = false;
      this.locked = false;
      this.scrollY = 0;
      this.bodyStyles = null;
      this.failsafe = null;
      this.inertElements = [];
      this.inertObserver = null;
      this.previouslyFocusedElement = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    initialiseSignatureLoader() {
      if (
        !this.element ||
        !this.mark ||
        this.isDesignMode() ||
        this.reducedMotion.matches
      ) {
        this.destroySignatureLoader();
        return;
      }

      if (this.readSessionFlag()) {
        this.playAbbreviatedIntro();
        return;
      }

      try {
        this.element.hidden = false;
        this.root.classList.add('signature-loader-pending', 'signature-loader-active');
        this.protectPageFocus();
        this.lockPage();
        this.writeSessionFlag();
        this.failsafe = window.setTimeout(() => this.destroySignatureLoader(), MAX_LOCK_DURATION);

        this.playFullSignatureIntro().catch(() => this.destroySignatureLoader());
      } catch (error) {
        this.destroySignatureLoader();
      }
    }

    async playFullSignatureIntro() {
      const readiness = this.prepareHomepage();
      const signatureStartedAt = performance.now();

      this.maskGuides.forEach((guide) => {
        const pathLength = guide.getTotalLength();
        const strokeWidth = Number(guide.getAttribute('stroke-width')) || 0;
        const safetyMargin = Math.max(strokeWidth, 2);
        const hiddenOffset = pathLength + safetyMargin;

        guide.dataset.signatureLoaderState = 'future';
        guide.style.opacity = '0';
        guide.style.visibility = 'hidden';
        guide.style.strokeDasharray = `${pathLength} ${pathLength + safetyMargin * 2}`;
        guide.style.strokeDashoffset = `${hiddenOffset}`;

        const activationTimer = window.setTimeout(() => {
          if (this.destroyed) return;

          guide.dataset.signatureLoaderState = 'active';
          guide.style.opacity = '1';
          guide.style.visibility = 'visible';

          const animation = guide.animate(
            [
              { strokeDashoffset: hiddenOffset },
              { strokeDashoffset: 0 },
            ],
            {
              duration: Number(guide.dataset.signatureLoaderDuration),
              easing: 'linear',
              fill: 'forwards',
            }
          );

          this.animations.push(animation);
          animation.finished
            .then(() => {
              if (this.destroyed) return;

              guide.dataset.signatureLoaderState = 'completed';
              guide.style.opacity = '1';
              guide.style.visibility = 'visible';
              guide.style.strokeDashoffset = '0';
            })
            .catch(() => undefined);
        }, Number(guide.dataset.signatureLoaderDelay));

        this.guideTimers.push(activationTimer);
      });

      this.animateInkBead(signatureStartedAt);

      await this.delay(1700);
      if (this.destroyed) return;

      this.animations.push(
        this.swimwear.animate(
          [
            { opacity: 0, transform: 'translateY(5px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          {
            duration: 300,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            fill: 'forwards',
          }
        )
      );

      await this.delay(600);
      if (this.destroyed) return;

      await readiness;
      this.element.classList.add('signature-loader--releasing');

      const markDissolve = this.mark.animate(
        [
          { opacity: 1, filter: 'blur(0)' },
          { opacity: 0, filter: 'blur(0.5px)' },
        ],
        {
          duration: 260,
          easing: 'cubic-bezier(0.4, 0, 0.6, 1)',
          fill: 'forwards',
        }
      );
      const overlayDissolve = this.element.animate(
        [{ opacity: 1 }, { opacity: 0 }],
        {
          duration: 400,
          delay: 60,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          fill: 'forwards',
        }
      );
      this.animations.push(markDissolve, overlayDissolve);

      await overlayDissolve.finished;
      this.destroySignatureLoader();
    }

    playAbbreviatedIntro() {
      this.destroySignatureLoader();
    }

    destroySignatureLoader() {
      if (this.destroyed) return;
      this.destroyed = true;

      window.clearTimeout(this.failsafe);
      window.clearTimeout(window.__sylavieSignatureLoaderFailsafe);
      this.guideTimers.forEach((timer) => window.clearTimeout(timer));
      this.guideTimers = [];
      window.cancelAnimationFrame(this.beadFrame);
      this.animations.forEach((animation) => animation.cancel());
      this.animations = [];

      if (this.element) {
        this.element.classList.add('signature-loader--releasing');
        this.element.style.pointerEvents = 'none';
        this.element.hidden = true;
        this.element.remove();
      }

      this.root.classList.remove(...ROOT_CLASSES);
      this.releasePageFocus();
      this.unlockPage();
    }

    animateInkBead(startedAt) {
      if (!this.bead || this.maskGuides.length === 0) return;

      const drawFrame = (now) => {
        if (this.destroyed) return;

        const elapsed = now - startedAt;
        const activeGuide = this.maskGuides.find((guide) => {
          const delay = Number(guide.dataset.signatureLoaderDelay);
          const duration = Number(guide.dataset.signatureLoaderDuration);
          return elapsed >= delay && elapsed < delay + duration;
        });

        if (activeGuide) {
          const delay = Number(activeGuide.dataset.signatureLoaderDelay);
          const duration = Number(activeGuide.dataset.signatureLoaderDuration);
          const progress = Math.min(1, Math.max(0, (elapsed - delay) / duration + 0.012));
          const pathLength = activeGuide.getTotalLength();
          const point = activeGuide.getPointAtLength(pathLength * progress);

          this.bead.setAttribute('cx', point.x.toFixed(2));
          this.bead.setAttribute('cy', point.y.toFixed(2));
          this.bead.style.opacity = '1';
        } else {
          this.bead.style.opacity = '0';
        }

        if (elapsed < 1550) {
          this.beadFrame = window.requestAnimationFrame(drawFrame);
        }
      };

      this.beadFrame = window.requestAnimationFrame(drawFrame);
    }

    prepareHomepage() {
      const readinessTasks = [];

      if (document.fonts && document.fonts.ready) {
        readinessTasks.push(document.fonts.ready.catch(() => undefined));
      }

      const heroImage = document.querySelector(
        '#MainContent img[fetchpriority="high"], #MainContent img:not([loading="lazy"])'
      );

      if (heroImage && typeof heroImage.decode === 'function' && !heroImage.complete) {
        readinessTasks.push(heroImage.decode().catch(() => undefined));
      }

      return Promise.race([
        Promise.all(readinessTasks),
        this.delay(2200),
      ]);
    }

    protectPageFocus() {
      if (!document.body || !this.element) return;

      const earlyFocusProtection = window.__sylavieSignatureLoaderFocusProtection;
      if (earlyFocusProtection) {
        earlyFocusProtection.observer?.disconnect();
        earlyFocusProtection.elements?.forEach((element) => {
          if (element instanceof HTMLElement && element.hasAttribute('inert')) {
            this.inertElements.push(element);
          }
        });
        window.__sylavieSignatureLoaderFocusProtection = null;
      }

      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLElement &&
        activeElement !== document.body &&
        !this.element.contains(activeElement)
      ) {
        this.previouslyFocusedElement = activeElement;
      }

      const makeInert = (element) => {
        if (
          !(element instanceof HTMLElement) ||
          element === this.element ||
          this.element.contains(element) ||
          element.hasAttribute('inert')
        ) {
          return;
        }

        element.setAttribute('inert', '');
        this.inertElements.push(element);
      };

      Array.from(document.body.children).forEach(makeInert);

      this.inertObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach(makeInert);
        });
      });
      this.inertObserver.observe(document.body, { childList: true });
    }

    releasePageFocus() {
      this.inertObserver?.disconnect();
      this.inertObserver = null;

      this.inertElements.forEach((element) => element.removeAttribute('inert'));
      this.inertElements = [];
      window.__sylavieReleaseSignatureLoaderFocus?.();

      if (this.previouslyFocusedElement?.isConnected) {
        this.previouslyFocusedElement.focus({ preventScroll: true });
      }
      this.previouslyFocusedElement = null;
    }

    lockPage() {
      if (this.locked || !document.body) return;

      this.scrollY = window.scrollY || window.pageYOffset;
      this.bodyStyles = {
        position: document.body.style.position,
        top: document.body.style.top,
        right: document.body.style.right,
        left: document.body.style.left,
        width: document.body.style.width,
      };

      document.body.classList.add('signature-loader-scroll-locked');
      Object.assign(document.body.style, {
        position: 'fixed',
        top: `-${this.scrollY}px`,
        right: '0',
        left: '0',
        width: '100%',
      });
      this.locked = true;
    }

    unlockPage() {
      if (!this.locked || !document.body) return;

      document.body.classList.remove('signature-loader-scroll-locked');
      Object.assign(document.body.style, this.bodyStyles);
      window.scrollTo(0, this.scrollY);
      this.locked = false;
    }

    readSessionFlag() {
      try {
        return window.sessionStorage.getItem(STORAGE_KEY) === 'true';
      } catch (error) {
        return false;
      }
    }

    writeSessionFlag() {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, 'true');
      } catch (error) {
        // Storage can be unavailable in strict privacy modes; the intro still runs safely.
      }
    }

    isDesignMode() {
      return Boolean(
        window.Shopify?.designMode ||
        this.root.classList.contains('shopify-design-mode')
      );
    }

    delay(duration) {
      return new Promise((resolve) => window.setTimeout(resolve, duration));
    }
  }

  let signatureLoaderInstance = null;

  function initialiseSignatureLoader() {
    if (signatureLoaderInstance) return;

    const element = document.querySelector(SELECTORS.loader);
    if (!element) {
      window.__sylavieReleaseSignatureLoaderFocus?.();
      document.documentElement.classList.remove(...ROOT_CLASSES);
      window.clearTimeout(window.__sylavieSignatureLoaderFailsafe);
      return;
    }

    signatureLoaderInstance = new SylavieSignatureLoader(element);
    signatureLoaderInstance.initialiseSignatureLoader();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseSignatureLoader, { once: true });
  } else {
    initialiseSignatureLoader();
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      signatureLoaderInstance?.destroySignatureLoader();
      window.__sylavieReleaseSignatureLoaderFocus?.();
      document.documentElement.classList.remove(...ROOT_CLASSES);
    }
  });

  window.addEventListener('pagehide', () => {
    signatureLoaderInstance?.destroySignatureLoader();
    window.__sylavieReleaseSignatureLoaderFocus?.();
  });

  window.addEventListener('sylavie:signature-loader-timeout', () => {
    signatureLoaderInstance?.destroySignatureLoader();
  });

  document.addEventListener('shopify:section:load', () => {
    if (window.Shopify?.designMode) {
      signatureLoaderInstance?.destroySignatureLoader();
      window.__sylavieReleaseSignatureLoaderFocus?.();
    }
  });
})();
